<script setup>
import { computed, ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import CampaignLayout from 'dashboard/components-next/Campaigns/CampaignLayout.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const { t } = useI18n();
const store = useStore();

const showDialog = ref(false);
const editingId = ref(null);
const searchQuery = ref('');
const categoryFilter = ref('');

const emptyForm = () => ({
  title: '',
  category: '',
  description: '',
  subject: '',
  body: '',
  thumbnail_url: '',
  is_default: false,
});

const formData = ref(emptyForm());

const uiFlags = computed(
  () => store.getters['emailCampaignTemplates/getUIFlags']
);
const isFetching = computed(() => uiFlags.value.isFetching);
const isSaving = computed(
  () => uiFlags.value.isCreating || uiFlags.value.isUpdating
);
const templates = computed(
  () => store.getters['emailCampaignTemplates/getEmailCampaignTemplates']
);

const categories = computed(() => {
  const set = new Set(templates.value.map(t => t.category).filter(Boolean));
  return Array.from(set).sort();
});

const filteredTemplates = computed(() => {
  const q = searchQuery.value.trim().toLowerCase();
  return templates.value.filter(tpl => {
    if (categoryFilter.value && tpl.category !== categoryFilter.value)
      return false;
    if (!q) return true;
    return (
      (tpl.title || '').toLowerCase().includes(q) ||
      (tpl.subject || '').toLowerCase().includes(q) ||
      (tpl.description || '').toLowerCase().includes(q)
    );
  });
});

const hasNoTemplates = computed(
  () => templates.value.length === 0 && !isFetching.value
);

onMounted(() => {
  store.dispatch('emailCampaignTemplates/get');
});

const openCreateDialog = () => {
  editingId.value = null;
  formData.value = emptyForm();
  showDialog.value = true;
};

const openEditDialog = tpl => {
  editingId.value = tpl.id;
  formData.value = {
    title: tpl.title || '',
    category: tpl.category || '',
    description: tpl.description || '',
    subject: tpl.subject || '',
    body: tpl.body || '',
    thumbnail_url: tpl.thumbnail_url || '',
    is_default: !!tpl.is_default,
  };
  showDialog.value = true;
};

const closeDialog = () => {
  showDialog.value = false;
  editingId.value = null;
  formData.value = emptyForm();
};

const handleSubmit = async () => {
  try {
    if (editingId.value) {
      await store.dispatch('emailCampaignTemplates/update', {
        id: editingId.value,
        email_campaign_template: formData.value,
      });
      useAlert(t('CAMPAIGN.EMAIL_TEMPLATES.UPDATE_SUCCESS'));
    } else {
      await store.dispatch('emailCampaignTemplates/create', {
        email_campaign_template: formData.value,
      });
      useAlert(t('CAMPAIGN.EMAIL_TEMPLATES.CREATE_SUCCESS'));
    }
    closeDialog();
  } catch (error) {
    useAlert(
      editingId.value
        ? t('CAMPAIGN.EMAIL_TEMPLATES.UPDATE_ERROR')
        : t('CAMPAIGN.EMAIL_TEMPLATES.CREATE_ERROR')
    );
  }
};

const handleDelete = async tpl => {
  if (!window.confirm(t('CAMPAIGN.EMAIL_TEMPLATES.CONFIRM_DELETE'))) return;
  try {
    await store.dispatch('emailCampaignTemplates/delete', tpl.id);
    useAlert(t('CAMPAIGN.EMAIL_TEMPLATES.DELETE_SUCCESS'));
  } catch (error) {
    useAlert(t('CAMPAIGN.EMAIL_TEMPLATES.DELETE_ERROR'));
  }
};

const formatDate = value => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString();
  } catch {
    return '';
  }
};

const isFormValid = computed(
  () =>
    formData.value.title.trim() &&
    formData.value.subject.trim() &&
    formData.value.body.trim()
);
</script>

<template>
  <CampaignLayout
    :header-title="t('CAMPAIGN.EMAIL_TEMPLATES.HEADER_TITLE')"
    :button-label="t('CAMPAIGN.EMAIL_TEMPLATES.NEW_TEMPLATE')"
    @click="openCreateDialog"
    @close="closeDialog"
  >
    <!-- Filters -->
    <div
      v-if="!isFetching && templates.length > 0"
      class="flex flex-col gap-3 mb-4 sm:flex-row sm:items-center"
    >
      <input
        v-model="searchQuery"
        type="text"
        class="flex-1 px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
        :placeholder="t('CAMPAIGN.EMAIL_TEMPLATES.SEARCH_PLACEHOLDER')"
      />
      <select
        v-model="categoryFilter"
        class="px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
      >
        <option value="">
          {{ t('CAMPAIGN.EMAIL_TEMPLATES.ALL_CATEGORIES') }}
        </option>
        <option v-for="c in categories" :key="c" :value="c">
          {{ c }}
        </option>
      </select>
    </div>

    <!-- Loading -->
    <div
      v-if="isFetching"
      class="flex items-center justify-center py-10 text-n-slate-11"
    >
      <Spinner />
    </div>

    <!-- Empty state -->
    <div
      v-else-if="hasNoTemplates"
      class="flex flex-col items-center justify-center gap-2 pt-14 text-slate-500"
    >
      <span class="text-3xl">📝</span>
      <p class="text-base font-medium">
        {{ t('CAMPAIGN.EMAIL_TEMPLATES.EMPTY_STATE.TITLE') }}
      </p>
      <p class="text-sm text-slate-400">
        {{ t('CAMPAIGN.EMAIL_TEMPLATES.EMPTY_STATE.SUBTITLE') }}
      </p>
    </div>

    <!-- Template grid -->
    <div v-else class="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
      <div
        v-for="tpl in filteredTemplates"
        :key="tpl.id"
        class="flex flex-col p-4 bg-white border rounded-xl border-slate-100 dark:bg-slate-800 dark:border-slate-700"
      >
        <img
          v-if="tpl.thumbnail_url"
          :src="tpl.thumbnail_url"
          alt=""
          class="object-cover w-full h-32 mb-3 rounded-lg bg-slate-100"
        />
        <div class="flex items-start justify-between gap-2">
          <div class="flex-1 min-w-0">
            <h3
              class="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate"
            >
              {{ tpl.title }}
            </h3>
            <p class="mt-1 text-xs text-slate-500 truncate">
              {{ tpl.subject }}
            </p>
          </div>
          <span
            v-if="tpl.is_default"
            class="px-2 py-0.5 text-xs font-medium text-green-700 bg-green-100 rounded-full"
          >
            {{ t('CAMPAIGN.EMAIL_TEMPLATES.CARD.DEFAULT') }}
          </span>
        </div>
        <p
          v-if="tpl.description"
          class="mt-2 text-xs text-slate-500 line-clamp-2"
        >
          {{ tpl.description }}
        </p>
        <div class="flex items-center justify-between mt-auto pt-3">
          <div class="flex items-center gap-2 text-xs text-slate-400">
            <span v-if="tpl.category" class="px-2 py-0.5 bg-slate-100 rounded">
              {{ tpl.category }}
            </span>
            <span>
              {{ t('CAMPAIGN.EMAIL_TEMPLATES.CARD.UPDATED') }}:
              {{ formatDate(tpl.updated_at) }}
            </span>
          </div>
          <div class="flex items-center gap-1">
            <button
              class="px-2 py-1 text-xs font-medium text-slate-600 bg-slate-100 rounded hover:bg-slate-200"
              @click="openEditDialog(tpl)"
            >
              {{ t('CAMPAIGN.EMAIL_TEMPLATES.CARD.EDIT') }}
            </button>
            <button
              class="px-2 py-1 text-xs text-red-500 hover:text-red-700"
              @click="handleDelete(tpl)"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Create / Edit dialog -->
    <div
      v-if="showDialog"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      @click.self="closeDialog"
    >
      <div
        class="w-full max-w-lg p-6 bg-white rounded-2xl shadow-xl dark:bg-slate-800 max-h-[90vh] overflow-y-auto"
      >
        <h3
          class="mb-4 text-base font-semibold text-slate-800 dark:text-slate-100"
        >
          {{
            editingId
              ? t('CAMPAIGN.EMAIL_TEMPLATES.EDIT_TITLE')
              : t('CAMPAIGN.EMAIL_TEMPLATES.CREATE_TITLE')
          }}
        </h3>
        <div class="flex flex-col gap-3">
          <div>
            <label
              class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
            >
              {{ t('CAMPAIGN.EMAIL_TEMPLATES.FORM.TITLE') }}
            </label>
            <input
              v-model="formData.title"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              :placeholder="
                t('CAMPAIGN.EMAIL_TEMPLATES.FORM.TITLE_PLACEHOLDER')
              "
            />
          </div>
          <div class="grid grid-cols-2 gap-3">
            <div>
              <label
                class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
              >
                {{ t('CAMPAIGN.EMAIL_TEMPLATES.FORM.CATEGORY') }}
              </label>
              <input
                v-model="formData.category"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
                :placeholder="
                  t('CAMPAIGN.EMAIL_TEMPLATES.FORM.CATEGORY_PLACEHOLDER')
                "
              />
            </div>
            <div>
              <label
                class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
              >
                {{ t('CAMPAIGN.EMAIL_TEMPLATES.FORM.THUMBNAIL') }}
              </label>
              <input
                v-model="formData.thumbnail_url"
                type="text"
                class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
                :placeholder="
                  t('CAMPAIGN.EMAIL_TEMPLATES.FORM.THUMBNAIL_PLACEHOLDER')
                "
              />
            </div>
          </div>
          <div>
            <label
              class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
            >
              {{ t('CAMPAIGN.EMAIL_TEMPLATES.FORM.DESCRIPTION') }}
            </label>
            <input
              v-model="formData.description"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              :placeholder="
                t('CAMPAIGN.EMAIL_TEMPLATES.FORM.DESCRIPTION_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label
              class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
            >
              {{ t('CAMPAIGN.EMAIL_TEMPLATES.FORM.SUBJECT') }}
            </label>
            <input
              v-model="formData.subject"
              type="text"
              class="w-full px-3 py-2 text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              :placeholder="
                t('CAMPAIGN.EMAIL_TEMPLATES.FORM.SUBJECT_PLACEHOLDER')
              "
            />
          </div>
          <div>
            <label
              class="block mb-1 text-xs font-medium text-slate-600 dark:text-slate-300"
            >
              {{ t('CAMPAIGN.EMAIL_TEMPLATES.FORM.BODY') }}
            </label>
            <textarea
              v-model="formData.body"
              rows="6"
              class="w-full px-3 py-2 font-mono text-sm border rounded-lg border-slate-200 dark:border-slate-600 dark:bg-slate-700"
              :placeholder="t('CAMPAIGN.EMAIL_TEMPLATES.FORM.BODY_PLACEHOLDER')"
            />
          </div>
          <label class="flex items-center gap-2 text-sm text-slate-600">
            <input v-model="formData.is_default" type="checkbox" />
            {{ t('CAMPAIGN.EMAIL_TEMPLATES.FORM.IS_DEFAULT') }}
          </label>
          <div class="flex justify-end gap-2 pt-2">
            <button
              class="px-3 py-1.5 text-sm text-slate-600 bg-slate-100 rounded-lg hover:bg-slate-200"
              @click="closeDialog"
            >
              {{ t('CAMPAIGN.EMAIL_TEMPLATES.FORM.CANCEL') }}
            </button>
            <button
              class="px-3 py-1.5 text-sm text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50"
              :disabled="!isFormValid || isSaving"
              @click="handleSubmit"
            >
              {{
                editingId
                  ? t('CAMPAIGN.EMAIL_TEMPLATES.FORM.UPDATE')
                  : t('CAMPAIGN.EMAIL_TEMPLATES.FORM.SAVE')
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </CampaignLayout>
</template>
